import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Sparkles, BadgeCheck, TrendingUp, ArrowRight } from 'lucide-react';
import { useAppSelector } from '../../hooks/redux';
import HeroStartForm from '../../components/home/HeroStartForm';
import BrandLogo from '../../components/BrandLogo';

export default function HomePage() {
  const content = useAppSelector((s) => s.geo.content);

  return (
    <>
      <section
        style={{
          position: 'relative',
          minHeight: 'calc(100vh - 140px)',
          background: '#FFFBF8',
          overflow: 'hidden',
          padding: '1.5rem 0 3rem',
        }}
      >
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', backgroundImage: 'radial-gradient(circle, rgba(194,24,91,0.12) 1.5px, transparent 1.5px)', backgroundSize: '28px 28px' }} />
        <div style={{ position: 'absolute', top: -100, right: -100, width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(233,30,99,0.18) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <div className="container-wide" style={{ position: 'relative', zIndex: 1 }}>
          <div className="home-hero-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 36, alignItems: 'center' }}>
            <div>
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'linear-gradient(135deg, #C2185B, #E91E63)', color: 'white', fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', padding: '6px 16px', borderRadius: 999 }}>
                  <Sparkles size={11} /> {content.hero.badge}
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 }}
                style={{ fontSize: 'clamp(2.2rem, 7vw, 4.6rem)', fontWeight: 900, lineHeight: 1.05, letterSpacing: '-0.03em', color: '#1A0A10', margin: '18px 0 12px' }}
              >
                {content.hero.titleLine1}
                <br />
                <span className="gradient-text">{content.hero.titleAccent}</span>
              </motion.h1>

              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.16 }} style={{ fontSize: 17, color: '#6B3A52', lineHeight: 1.65, maxWidth: 540, marginBottom: 12 }}>
                {content.hero.subtitle}
              </motion.p>
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} style={{ fontSize: 14, color: '#C2185B', fontWeight: 600, marginBottom: 16 }}>
                {content.hero.note}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.22 }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 22,
                  padding: '10px 14px',
                  borderRadius: 14,
                  background: 'linear-gradient(135deg, rgba(194,24,91,0.12), rgba(233,30,99,0.08))',
                  border: '1px solid #F0C8D8',
                }}
              >
                <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'white', background: 'linear-gradient(135deg,#C2185B,#E91E63)', padding: '5px 10px', borderRadius: 999 }}>
                  {content.hero.comboHighlight}
                </span>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: '#6B3A52' }}>
                  Credit rating + capital raise — one continuous journey
                </span>
              </motion.div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 28 }}>
                {content.hero.chips.map((chip) => (
                  <span key={chip} style={{ fontSize: 12, fontWeight: 600, color: '#6B3A52', background: 'rgba(255,255,255,0.9)', border: '1px solid #F0C8D8', padding: '6px 14px', borderRadius: 999 }}>
                    {chip}
                  </span>
                ))}
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <Link to="/how-it-works" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 22px', fontWeight: 700, color: '#C2185B', border: '2px solid #C2185B', borderRadius: 999, background: 'rgba(255,255,255,0.85)' }}>
                  How it works <ArrowRight size={16} />
                </Link>
                <Link to="/credit-rating" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 22px', fontWeight: 700, color: '#6B3A52' }}>
                  <BadgeCheck size={16} color="#C2185B" /> Rating policy
                </Link>
              </div>
            </div>

            <motion.div initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.12 }}>
              <HeroStartForm />
            </motion.div>
          </div>
        </div>
      </section>

      <section style={{ background: 'white', padding: '72px 0', borderTop: '1px solid #F0C8D8' }}>
        <div className="container-wide">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
            <BrandLogo size={36} />
            <h2 style={{ fontSize: 28, fontWeight: 800, margin: 0 }}>How Micus works</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20 }}>
            {content.howItWorks.map((step, i) => (
              <div key={step.title} style={{ background: '#FFFBF8', border: '1px solid #F0C8D8', borderRadius: 18, padding: 24 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg,#C2185B,#E91E63)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, marginBottom: 14 }}>
                  {i + 1}
                </div>
                <h3 style={{ fontSize: 17, fontWeight: 800, margin: '0 0 8px' }}>{step.title}</h3>
                <p style={{ fontSize: 14, color: '#6B3A52', margin: 0, lineHeight: 1.6 }}>{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ background: '#FFF0F5', padding: '72px 0' }}>
        <div className="container-wide" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
          <div style={{ background: 'white', borderRadius: 20, border: '1px solid #F0C8D8', padding: 28 }}>
            <BadgeCheck size={22} color="#C2185B" />
            <h3 style={{ fontSize: 20, fontWeight: 800, margin: '12px 0 8px' }}>{content.ratingPolicy.title}</h3>
            <p style={{ fontSize: 14, color: '#6B3A52', lineHeight: 1.65 }}>{content.ratingPolicy.summary}</p>
            <Link to="/credit-rating" style={{ color: '#C2185B', fontWeight: 700, fontSize: 14 }}>Read policy →</Link>
          </div>
          <div style={{ background: 'white', borderRadius: 20, border: '1px solid #F0C8D8', padding: 28 }}>
            <TrendingUp size={22} color="#C2185B" />
            <h3 style={{ fontSize: 20, fontWeight: 800, margin: '12px 0 8px' }}>{content.fundraisingProcedure.title}</h3>
            <p style={{ fontSize: 14, color: '#6B3A52', lineHeight: 1.65 }}>{content.fundraisingProcedure.summary}</p>
            <Link to="/fundraising" style={{ color: '#C2185B', fontWeight: 700, fontSize: 14 }}>See procedure →</Link>
          </div>
        </div>
      </section>

      <style>{`
        @media (min-width: 1024px) {
          .home-hero-grid { grid-template-columns: 1.1fr 0.9fr !important; gap: 48px !important; }
        }
      `}</style>
    </>
  );
}
